import type { ShopperAiScope } from "../../keyboard-shortcuts/shopper-ai-shortcuts";
import type { ScopeNode } from "../scope-tree";
import {
  supportTicketActionIds,
  type SupportTicketActionId,
} from "./action-ids";

export const priorityScope: ScopeNode<SupportTicketActionId, ShopperAiScope> = {
  id: "priority",
  label: "Set Priority",
  actionRefs: [
    { actionId: supportTicketActionIds.priorityUrgent },
    { actionId: supportTicketActionIds.priorityClear },
  ],
};

export const snoozeScope: ScopeNode<SupportTicketActionId, ShopperAiScope> = {
  id: "snooze",
  label: "Snooze",
  actionRefs: [
    { actionId: supportTicketActionIds.snooze1h },
    { actionId: supportTicketActionIds.snooze4h },
    { actionId: supportTicketActionIds.snooze24h },
    { actionId: supportTicketActionIds.snoozeCustom },
  ],
};

/**
 * Assignee selection scope.
 * Actions are populated at runtime from team members.
 */
export const assignScope: ScopeNode<SupportTicketActionId, ShopperAiScope> = {
  id: "assign",
  label: "Assign",
  actionRefs: [], // Populated at runtime
};

/**
 * Tag selection scope.
 * Actions are populated at runtime from available tags.
 */
export const addTagScope: ScopeNode<SupportTicketActionId, ShopperAiScope> = {
  id: "addTag",
  label: "Add Tag",
  actionRefs: [], // Populated at runtime
};

/**
 * Main action menu scope - Alt+<key> shortcuts.
 * Contains direct actions and submenu triggers.
 */
export const actionMenuScope: ScopeNode<SupportTicketActionId, ShopperAiScope> =
  {
    id: "actionMenu",
    label: "Actions",
    actionRefs: [
      { actionId: supportTicketActionIds.close },
      { actionId: supportTicketActionIds.reopen },
      { actionId: supportTicketActionIds.snooze, childScope: snoozeScope },
      { actionId: supportTicketActionIds.markRead },
      { actionId: supportTicketActionIds.markUnread },
      {
        actionId: supportTicketActionIds.setPriority,
        childScope: priorityScope,
      },
      { actionId: supportTicketActionIds.assign, childScope: assignScope },
      { actionId: supportTicketActionIds.addTag, childScope: addTagScope },
      { actionId: supportTicketActionIds.archive },
      { actionId: supportTicketActionIds.delete },
    ],
  };

/**
 * Support ticket table navigation scope - vim-style keys.
 * Active when focus is on the support ticket list.
 */
export const supportTicketsTableScope: ScopeNode<
  SupportTicketActionId,
  ShopperAiScope
> = {
  id: "supportTicketsTable",
  label: "Support Ticket List",
  actionRefs: [
    { actionId: supportTicketActionIds.navigateUp },
    { actionId: supportTicketActionIds.navigateDown },
    { actionId: supportTicketActionIds.openSupportTicket },
    {
      actionId: supportTicketActionIds.openActionMenu,
      childScope: actionMenuScope,
    },
  ],
};

/**
 * Message input scope - keyboard shortcuts for sending and managing drafts.
 * Active when focus is in the message input editor.
 */
export const messageInputScope: ScopeNode<
  SupportTicketActionId,
  ShopperAiScope
> = {
  id: "messageInput",
  label: "Message Input",
  actionRefs: [
    { actionId: supportTicketActionIds.sendMessage },
    { actionId: supportTicketActionIds.deleteDraft },
    { actionId: supportTicketActionIds.reply },
    { actionId: supportTicketActionIds.replyAll },
    { actionId: supportTicketActionIds.forward },
  ],
};
